import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    HostBinding
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { GfMarkdownEditorComponent } from '../../gf-markdown-editor/gf-markdown-editor.component';
import { MentionListConfig } from '../../../core/services/core.mentio.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { UiService } from '../../../core/services/core.ui.service';
import { WikiConstants, WikiContentFormat, WikiEditorMode, WikiPreviewStyle } from '../../../plugins/wiki/wiki.constants';
import { WikiEditHelpModalComponent } from '../edit-help/wiki.edit-help.modal.component';
import { WikiImportResult } from '../import/wiki.import.component';
import { WikiImportMode, WikiImportModeModalComponent } from '../import/wiki.import-mode-modal.component';
import { FileTypeService } from '../../../plugins/tracker/services/file-type.service';

/**
 * The wiki page editor: a full-page editing surface with its own chrome.
 *
 * The ToastUI integration itself is `gf-markdown-editor`, nested inside — this
 * component owns only what a wiki page needs and a compose box does not: the
 * Side-by-side / Raw / WYSIWYG / Save / Cancel / Rename / Guide bar, the
 * attachment insertion, and the Markdown help modal.
 *
 * It used to build its own ToastUI editor, duplicating ~600 lines of the
 * sibling's lifecycle — creation, teardown, vDOM error suppression, paste
 * auto-switch, mode preferences, image upload, video embeds. Every fix had to
 * be applied twice. Now there is one integration and two skins over it.
 *
 * ToastUI-level operations that the child does not expose reach the instance
 * through its `(editorReady)` output; see {@link onInnerReady}.
 */
@Component({
    selector: 'wiki-markdown-editor',
    template: require('./wiki.markdown-editor.component.html')
})
export class WikiMarkdownEditorComponent {
    /** The nested editor. Everything ToastUI is its business. */
    @ViewChild('inner', { static: false }) inner!: GfMarkdownEditorComponent;

    /** The markdown content to edit */
    @Input() content: string = '';

    /** Content type MIME: 'text/markdown' or 'text/html'. If 'text/html', content is loaded via setHTML */
    @Input() contentFormat: WikiContentFormat = WikiConstants.ContentFormat.MARKDOWN;

    /** Wiki page ID for image uploads */
    @Input() wikiPageId: number = 0;

    /**
     * Section for image uploads. Also decides the Raw/WYSIWYG preference key —
     * both 'wikipage' and 'wikifiles' resolve to `mdeditor_wiki`, so it must be
     * forwarded to the child verbatim or users lose their saved mode.
     */
    @Input() uploadSection: string = 'wikipage';

    /** Preference section key used to persist Raw/WYSIWYG mode. */
    @Input() preferenceSection: string = '';

    /** Whether the editor is read-only */
    @Input() readOnly: boolean = false;

    /** Initial edit type: 'markdown' or 'wysiwyg' */
    @Input() initialEditType: WikiEditorMode = WikiConstants.EditorMode.WYSIWYG;

    /** Preview style: 'tab' or 'vertical' */
    @Input() previewStyle: WikiPreviewStyle = WikiConstants.PreviewStyle.VERTICAL;

    /** Editor height */
    @Input() height: string = '500px';

    /** Enable inline mode (minimal UI, auto height) */
    @Input() inlineMode: boolean = false;

    /** Bind inlineMode to host element class so global CSS selectors match */
    @HostBinding('class.inline-mode') get hostInlineMode() { return this.inlineMode; }

    /**
     * In inline mode the toolbar hides on blur by default. Set false to keep it
     * visible (used by the full-page wiki editor).
     */
    @Input() autoHideToolbar: boolean = true;

    /** Bind persistent-toolbar to host so CSS can keep the inline toolbar shown */
    @HostBinding('class.persistent-toolbar') get hostPersistentToolbar() {
        return this.inlineMode && !this.autoHideToolbar;
    }

    /** Show the mode switcher button in inline mode */
    @Input() showModeSwitch: boolean = true;

    /**
     * Which shared toolbar to render — see MarkdownEditorService.buildToolbarItems.
     * Wiki pages and notebook pages are document-scale content, so this defaults
     * to the full set; 'essential' is the escape hatch for a constrained host.
     */
    @Input() toolbarVariant: 'full' | 'essential' = 'full';

    /**
     * @-mention candidates, as built by MentionsService.createMentionConfig().
     *
     * Empty (the default) turns mentions off, which is what a host with no
     * roster to offer wants — the personal notebook has no project members to
     * suggest and nowhere to send a notification.
     */
    @Input() mentions: MentionListConfig[] = [];

    /** Emits when content changes */
    @Output() contentChange = new EventEmitter<string>();

    /**
     * Emits the text typed after '@', so the host can look candidates up and
     * feed the result back through `[mentions]`.
     */
    @Output() searchTerm = new EventEmitter<string>();

    /** Emits when editor is ready */
    @Output() editorReady = new EventEmitter<any>();

    /** Emits when save is triggered (Ctrl+S or toolbar button) */
    @Output() onSave = new EventEmitter<string>();

    /** Emits when cancel button is clicked */
    @Output() onCancel = new EventEmitter<void>();

    /**
     * Off by default: only a host editing a document that can be renamed (the
     * wiki page editor) turns it on, and that host decides whether the current
     * page qualifies.
     */
    @Input() showRename: boolean = false;

    @Output() onRename = new EventEmitter<void>();

    /**
     * Off by default, like showRename: only a host editing a saved wiki page
     * turns it on. Shows the "Import document" control, which converts a Word,
     * PowerPoint, Excel or PDF file into content for this page.
     */
    @Input() showImport: boolean = false;

    /**
     * Emits each import once it has been applied to the editor, so the host can
     * discard its pending pictures if the edit is cancelled rather than saved.
     */
    @Output() documentImported = new EventEmitter<WikiImportResult>();

    /** What the next import should do with content already in the editor. */
    private importMode: WikiImportMode = 'replace';

    /** Emits when help toggle is requested on wide screens */
    @Output() onHelpToggle = new EventEmitter<void>();

    /** Emits when user activates the Insert Attachment toolbar command */
    @Output() insertAttachmentRequest = new EventEmitter<void>();

    /** Label for the save button (e.g. 'Save', 'Save changes?', 'Accept markdown conversion?') */
    @Input() saveButtonLabel: string = 'Save';

    /** When true, the save button uses warning styling instead of success */
    @Input() isWarningSave: boolean = false;

    /** Whether the help sidebar is currently open (for toolbar button active state) */
    @Input() helpPanelOpen: boolean = false;

    get helpButtonActive(): boolean {
        return this.helpPanelOpen && window.innerWidth >= 992;
    }

    /**
     * The raw ToastUI instance, for the handful of operations the child does not
     * expose. Null until the child reports it — see {@link isReady}.
     */
    private editor: any = null;

    /** Constants exposed for template binding */
    public readonly WikiConstants = WikiConstants;

    /** Whether side-by-side mode is active */
    public sideBySide: boolean = false;

    constructor(
        private modalService: NgbModal,
        private fileTypeService: FileTypeService,
        private gfTranslateService: GfTranslateService,
        private uiService: UiService,
    ) {}

    // ─────────────────────────── child wiring ───────────────────────────

    /** Height ToastUI is built with. Inline hosts grow with their content. */
    get editorHeight(): string {
        return this.inlineMode ? 'auto' : this.height;
    }

    get editorMinHeight(): string {
        return this.inlineMode ? '150px' : '300px';
    }

    /** Inline mode has no room for the side-by-side pane at startup. */
    get editorPreviewStyle(): WikiPreviewStyle {
        return this.inlineMode ? WikiConstants.PreviewStyle.TAB : this.previewStyle;
    }

    /**
     * Buttons the child renders only because this component registers the
     * commands behind them in {@link onInnerReady}. Scroll-sync and save belong
     * to the full-page bar, not the inline one, which has its own Save button.
     */
    get editorToolbarOptions(): { attachment: boolean; scrollSync: boolean; save: boolean } {
        return {
            attachment: this.uploadSection === 'wikipage',
            scrollSync: !this.inlineMode,
            save: !this.inlineMode,
        };
    }

    /** The mode the nested editor is in; drives the toggle's active state. */
    get currentMode(): WikiEditorMode {
        return this.inner?.currentMode || WikiConstants.EditorMode.MARKDOWN;
    }

    /**
     * Whether the nested ToastUI editor exists yet.
     *
     * The host's save-on-leave path reads {@link getMarkdown} on every change
     * detection cycle, and an editor that has not mounted answers `''` — which
     * compared against a stored page looks like "the user deleted everything".
     * Hosts gate on this so an unmounted editor reports *no reading* rather than
     * an empty document.
     */
    get isReady(): boolean {
        return !!this.editor;
    }

    /**
     * The child mounted ToastUI. Register the commands its toolbar buttons
     * dispatch, which are this component's to own: it raises the events behind
     * them.
     */
    onInnerReady(editor: any): void {
        this.editor = editor;

        // ToastUI resolves a button's command per mode, so one registered for
        // markdown only silently does nothing in WYSIWYG.
        [WikiConstants.EditorMode.MARKDOWN, WikiConstants.EditorMode.WYSIWYG].forEach((mode) => {
            editor.addCommand(mode, 'save', () => {
                this.triggerSave();
                return true;
            });
            editor.addCommand(mode, 'insertAttachment', () => {
                this.insertAttachmentRequest.emit();
                return true;
            });
        });

        this.editorReady.emit(editor);
    }

    // ─────────────────────────── the chrome ───────────────────────────

    /**
     * Trigger a save event.
     *
     * Reads the child rather than this component's `content` input: the input
     * trails the debounced `(contentChange)` by up to 300ms, so a save fired
     * straight after a keystroke would drop the tail of the edit.
     */
    triggerSave(): void {
        if (!this.isReady) { return; }
        this.onSave.emit(this.getMarkdown());
    }

    /**
     * Open the Markdown syntax guide modal.
     */
    showMarkdownHelp(): void {
        if (window.innerWidth < 992) {
            const modalRef = this.modalService.open(WikiEditHelpModalComponent, { size: 'lg', windowClass: 'wiki-help-modal-window' });
            modalRef.componentInstance.onClose.subscribe(() => modalRef.close());
            return;
        }
        this.onHelpToggle.emit();
    }

    /**
     * Asked by the import control before its file picker opens. A page that
     * already has content gets a choice — replace, append, or cancel — and the
     * question comes before the upload so a cancelled import leaves nothing behind.
     * Bound as an arrow function because the control calls it detached.
     */
    confirmImportMode = (): Promise<boolean> => {
        if (!this.getMarkdown().trim()) {
            this.importMode = 'replace';
            return Promise.resolve(true);
        }
        const modalRef = this.modalService.open(WikiImportModeModalComponent, {
            backdrop: 'static',
            keyboard: true
        });
        return modalRef.result.then(
            (mode: WikiImportMode) => {
                this.importMode = mode === 'append' ? 'append' : 'replace';
                return mode === 'append' || mode === 'replace';
            },
            () => false
        );
    };

    /**
     * A document was converted: apply it as the page's content, per the choice
     * made before the upload. Nothing is saved until the host's save runs.
     */
    onDocumentImported(result: WikiImportResult): void {
        if (!this.isReady) { return; }
        const existing = this.getMarkdown().replace(/\s+$/, '');
        const content = this.importMode === 'append' && existing
            ? existing + '\n\n' + result.content
            : result.content;
        this.setMarkdown(content);
        this.importMode = 'replace';
        this.documentImported.emit(result);

        const title = this.gfTranslateService.translate('Import document');
        (result.warnings || []).forEach((w: string) => this.uiService.message.showWarning(w, title));
        this.uiService.message.showSuccess(
            this.gfTranslateService.translate(
                '{{fileName}} was converted into this page. Review the result, then save: its pictures are kept only when the page is saved.',
                { fileName: result.fileName }),
            title
        );
    }

    /**
     * Set the editor to a specific mode.
     *
     * Wider than the child's `setMode`: picking a mode from this bar also leaves
     * side-by-side, which is a preview style rather than a mode and so is
     * invisible to the child.
     */
    public setMode(mode: WikiEditorMode): void {
        if (!this.isReady) { return; }
        this.sideBySide = false;
        this.editor.changePreviewStyle(WikiConstants.PreviewStyle.TAB);
        this.inner.setMode(mode);
    }

    /**
     * Toggle side-by-side mode (markdown with a vertical preview pane).
     *
     * Preview style is a ToastUI-level concern the child has no API for, so this
     * drives the instance directly.
     */
    public toggleSideBySide(): void {
        if (!this.isReady) { return; }
        if (this.sideBySide) {
            this.sideBySide = false;
            this.editor.changePreviewStyle(WikiConstants.PreviewStyle.TAB);
            return;
        }
        this.sideBySide = true;
        this.inner.setMode(WikiConstants.EditorMode.MARKDOWN);
        this.editor.changePreviewStyle(WikiConstants.PreviewStyle.VERTICAL);
    }

    /**
     * Insert an attachment file reference at the cursor.
     * In WYSIWYG mode uses exec() to create proper image/link nodes.
     * In markdown mode uses insertText() with markdown syntax.
     */
    public insertAttachment(file: any): void {
        if (!this.isReady) { return; }
        const url = file.attachmentUrl;
        const name = file.fileName || 'attachment';
        const isImage = this.fileTypeService.isImage(file);
        if (this.currentMode === WikiConstants.EditorMode.WYSIWYG) {
            if (isImage) {
                this.editor.exec('addImage', { imageUrl: url, altText: name });
            } else {
                this.editor.exec('addLink', { linkUrl: url, linkText: name });
            }
        } else {
            this.editor.insertText(isImage ? `![${name}](${url})` : `[${name}](${url})`);
        }
    }

    // ─────────────────────────── delegated API ───────────────────────────

    /**
     * Current markdown, or '' before the editor mounts.
     *
     * Callers that would act on the difference between '' and "unread" — the
     * wiki edit lifecycle, which decides whether the page has unsaved changes —
     * must gate on {@link isReady} first.
     */
    public getMarkdown(): string {
        return this.inner?.getMarkdown() || '';
    }

    public setMarkdown(markdown: string): void {
        this.inner?.setMarkdown(markdown);
    }

    public focus(): void {
        this.inner?.focus();
    }

    public insertText(text: string): void {
        this.inner?.insertText(text);
    }
}
